import { useCallback, useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { useIntl } from 'react-intl'
import { sortBy } from 'lib/array/arrayUtils'
import useFlightFareLabels from 'hooks/Flights/useFlightFareLabels'
import useFlightDeals from 'hooks/Flights/useFlightDeals'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import TextLink from 'LuxKit/TextLink'
import Group from 'components/utils/Group'
import Image from 'components/Common/Image'
import BodyText from 'LuxKit/Typography/BodyText'
import FormatCurrency from 'components/Common/FormatCurrency'
import FlightDealLabel from 'components/OfferPage/FlightDeal/FlightDealLabel'
import { useScreenSizeOnly } from 'hooks/useScreenSize'

const OuterBanner = styled.div`
  padding: ${rem(16)};
  background-color: ${props => props.theme.palette.highlight.primary.lightBackground};
`

const InnerBanner = styled.div`
  padding: ${rem(16)};
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  border-radius: ${props => props.theme.borderRadius.M};
`

interface Props {
  originAirportCode: string
  destinationAirportCode: string
}

function OfferPageFlightDealBanner(props: Props) {
  const { destinationAirportCode, originAirportCode } = props
  const intl = useIntl()
  const { fareTypeLabelMap } = useFlightFareLabels()

  const isMobile = useScreenSizeOnly('mobile')
  const dispatch = useOfferPageDispatch()
  const [deals] = useFlightDeals('all', {
    originAirportCode,
    destinationAirportCode,
  })

  const activeDeal = useMemo(() => {
    return sortBy(deals, (deal) => deal.perAdultPrice, 'asc')[0]
  }, [deals])

  const handleCheckAvailability = useCallback(() => {
    dispatch({ type: OfferPageActions.toggleFilter, filter: 'checkin' })
    dispatch({ type: OfferPageActions.setFlights, flights: true })
  }, [dispatch])

  if (!activeDeal || isMobile) {
    return null
  }

  const fromPricePerAdult = intl.formatMessage(
    {
      id: 'HKHIshendNVHZZ_WWg4M',
      defaultMessage: 'From {price}/adult',
      description: 'Price display showing the starting price per adult for flight search result cards',
    },
    {
      price: (
        <strong>
          <FormatCurrency value={activeDeal.perAdultPrice} format="roundedDollar" />
        </strong>
      ),
    },
  )

  const flightDealSummary = intl.formatMessage(
    {
      id: 'EM56NGZFjTO6em5aUcQX',
      defaultMessage: '{carrierName} {origin}-{destination} {fareType} {price}',
      description: 'Flight deal summary showing carrier name, route, fare type, and price per adult',
    },
    {
      carrierName: activeDeal.carrier.name,
      origin: originAirportCode,
      destination: destinationAirportCode,
      fareType: fareTypeLabelMap[activeDeal.fareType],
      price: fromPricePerAdult,
    },
  )

  return (
    <OuterBanner>
      <InnerBanner>
        <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="center" gap={8}>
          <Group direction="horizontal" verticalAlign="center" gap={16}>
            <FlightDealLabel activeDeal={activeDeal} showCountdown />
            <Group direction="horizontal" verticalAlign="end" gap={8}>
              {(activeDeal.carrier.brandMark || activeDeal.carrier.logo) && <Image image={activeDeal.carrier.brandMark || activeDeal.carrier.logo} height={24} />}
              <BodyText variant="medium">
                {flightDealSummary}
              </BodyText>
            </Group>
          </Group>
          <TextLink size="small" colour="light-foreground-alt" weight="regular" onClick={handleCheckAvailability}>
            {intl.formatMessage({
              id: '7R2gXK8MsBe5_PKUubtN',
              defaultMessage: 'Check availability',
              description: 'Action button label to check availability in the flights section',
            })}
          </TextLink>
        </Group>
      </InnerBanner>
    </OuterBanner>
  )
}

export default OfferPageFlightDealBanner
